import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import REIDataTable from "../../../components/ui/dataTable";
import { WatchListService } from "../../../services/watchListService";
import ViewPropertyDetails from "../../../interface/Watchlist/propertyDetails";
import { ColumnType } from "../../../utils/constants";
import { httpPost } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { resetSalesListingData, setSalesListingData } from "../../../store/slice/investarSearchSlice";
import { useDispatch, useSelector } from "react-redux";
import Graph from "../../../components/ui/graph";
import { calculatePercentageShare } from "../../../utils/commonUtil";
import { IREIGraphColor, IREIGraphDataSet } from "../../../interface/graph";
import { RootState } from "../../../store";
import { useNavigate } from "react-router-dom";

interface PropertyDetails {
  propertyAnalysis: ViewPropertyDetails;
}

const PropertyAnalysis: React.FC<PropertyDetails> = ({ propertyAnalysis }) => {
  const [range, setRange] = useState([]);
  const dispatch = useDispatch();
  const includedKeys = propertyAnalysis.isFromPropertyAnalyser
    ? ["Mortgaged", "Owned", "Renting", "Population"]
    : ["FullyOwned", "Purchasing", "Rented", "Population"];
  const labels = propertyAnalysis.isFromPropertyAnalyser
    ? Object.keys(propertyAnalysis.AnalyserPostcodeStatistics).filter((key) => includedKeys.includes(key))
    : Object.keys(propertyAnalysis.PostcodeStatistics).filter((key) => includedKeys.includes(key));
  const graphData = labels.map((key) =>
    propertyAnalysis.isFromPropertyAnalyser
      ? propertyAnalysis.AnalyserPostcodeStatistics[key]
      : propertyAnalysis.PostcodeStatistics[key]
  );
  const isGraphVisible = graphData.filter((item) => item != 0).length == 0 ? false : true;
  const calculatedGraphData = calculatePercentageShare(graphData, labels);
  const totalUnitSales = useRef<number>(0);
  const totalRentalListings = useRef<number>(0);
  const salesListingData = useSelector((state: RootState) => state.investar.salesListingData);
  const navigation = useNavigate();
  const populationIndex = labels.findIndex((label) => label === "Population");

  useEffect(() => {
    if (salesListingData) {
      navigation("/");
    }
  }, [salesListingData]);

  useEffect(() => {
    WatchListService.getProperties().then((data) => setRange(data));
    totalUnitSales.current = propertyAnalysis.SalesPriceRange.reduce((acc, curr) => acc + curr.Count, 0);
    totalRentalListings.current = propertyAnalysis.RentalPriceRange.reduce((acc, curr) => acc + curr.Count, 0);
  }, []);

  const rentalListingColumn = [
    {
      field: "Label",
      position: "left",
    },
    {
      field: "Count",
      position: "right",
    },
  ];

  const getAllSalesListing = async (_event: any, rowData: any) => {
    const isSearchByCategory = !rowData?.ValueFrom;
    const { Country, StateRegion, Postcode, Suburb, Category } = propertyAnalysis.PropertyDetails;
    let dwellingType = rowData?.DwellingType || Category;
    if (!isSearchByCategory) {
      switch (dwellingType) {
        case "House":
        case "Commercial":
        case "Rural":
        case "Land":
        case "Other":
          dwellingType = "House";
          break;
        case "Unit":
        case "Unit/Apartment":
        case "Block of Units":
          dwellingType = "Unit";
          break;
        case "Townhouse":
          dwellingType = "Townhouse";
          break;
        default:
          dwellingType = "House";
      }
    } else {
      switch (dwellingType) {
        case "House":
        case "Commercial":
        case "Rural":
        case "Other":
          dwellingType = "House";
          break;
        case "Unit":
        case "Unit/Apartment":
        case "Block of Units":
          dwellingType = "Unit";
          break;
        case "Townhouse":
          dwellingType = "Townhouse";
          break;
        case "Land":
          dwellingType = isSearchByCategory ? "Land" : "House";
          break;
        case "Block":
        case "Multi Dwelling":
          dwellingType = "Block";
          break;
        case "":
          dwellingType = "";
          break;
        default:
          dwellingType = "Unit";
      }
    }

    const propertyListing: any = {
      CountryCode: Country,
      StateRegion,
      District: "",
      PostCode: rowData?.DwellingType == "" ? "" : Postcode,
      Suburb,
      Category: dwellingType,
      ...(!isSearchByCategory && {
        ValueFrom: rowData.ValueFrom == "0" ? "" : rowData.ValueFrom,
        ValueTo: rowData.ValueTo == "0" ? "" : rowData.ValueTo,
      }),
    };

    const apiURL = isSearchByCategory
      ? `${ApiEndPoints.searchSummary}${Country}/Search/AllSales/`
      : `${ApiEndPoints.searchSummary}${Country}/Search/CategoryTypeSaleListings/`;

    try {
      const result = await httpPost(apiURL, propertyListing);
      if (result.status === 200) {
        dispatch(setSalesListingData(result?.data));
      }
    } catch (error) {
      console.error("Error fetching sales listings:", error);
    }
  };

  const allSalesListingColumns = [
    {
      field: "Label",
      position: "left",
    },
    {
      field: "Count",
      position: "right",
      type: ColumnType.link,
      bodyTemplate: (rowData: any) =>
        rowData.Label === "Other" ? (
          <span className="text-gray-500 text-xs md:text-sm">{rowData.Count}</span>
        ) : (
          <div className="flex flex-row w-full text-[#4e9fba]">
            <span
              className="mr-1 pt-2 cursor-pointer text-xs md:text-sm"
              onClick={(_event: any) => {
                getAllSalesListing(_event, rowData);
              }}
            >
              {rowData.Count}
            </span>
          </div>
        ),
    },
  ];

  const graphLabel = (
    <div className={`text-center`}>
      <span className="text-sm md:text-base font-semibold">Postcode statistics</span>
      <br />
      <span className="text-xs md:text-sm font-medium">Postcode: {`${propertyAnalysis.PropertyDetails.Postcode}`}</span>
    </div>
  );

  const customOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: () => [],
          label: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const filteredLabels = labels.filter((_, idx) => idx !== populationIndex);
            const filteredPercentages = calculatedGraphData.filter((_, idx) => idx !== populationIndex);
            const label = filteredLabels[index];
            const percent = filteredPercentages[index];
            return `${label}: ${percent}%`;
          },
        },
      },
      scales: {
        y: {
          grid: {
            display: false,
          },
          beginAtZero: true,
        },
        x: {
          grid: {
            display: false,
          },
        },
      },
    },
  };

  const rederGraphColors = (): IREIGraphColor => {
    let colors: string[] = [];
    let border: string[] = [];

    ((colors = [
      "rgba(255, 159, 64, 0.2)",
      "rgba(75, 192, 192, 0.2)",
      "rgba(54, 162, 235, 0.2)",
      "rgba(153, 102, 255, 0.2)",
      "rgba(255, 99, 132, 0.2)",
    ]),
      (border = ["rgba(255, 255, 255, 1)"]));
    return { backgroundColor: colors, borderColor: border };
  };

  const getPieChartStatistics = () => {
    const statistics = graphData.map((item, index) => {
      const populationIndex = labels.findIndex((x) => x == "Population");
      if (index == graphData.length - 1) {
        return (
          <div key={index} className="font-medium italic self-center">
            Population : {graphData.find((data, index) => index == populationIndex)}
          </div>
        );
      } else if (item != 0) {
        if (index == populationIndex) {
          return (
            <div key={index} className="self-center">
              <i className="pi pi-stop" style={{ backgroundColor: rederGraphColors().backgroundColor[index] }}></i>
              <span className="text-[10px] md:text-xs text-left align-middle">
                {` ${calculatedGraphData[populationIndex + 1]}%  ${labels[populationIndex + 1]} `}
                {<br />}
              </span>
            </div>
          );
        } else {
          return (
            <div key={index} className="self-center">
              <i className="pi pi-stop" style={{ backgroundColor: rederGraphColors().backgroundColor[index] }}></i>
              <span className="text-[10px] md:text-xs text-left align-middle">
                {` ${calculatedGraphData[index]}%  ${labels[index]} `}
                {<br />}
              </span>
            </div>
          );
        }
      }
    });

    return statistics;
  };

  const renderDataSet = (): IREIGraphDataSet[] => {
    const dataSet = [
      {
        data: graphData.filter((_, index) => index !== populationIndex),
        backgroundColor: rederGraphColors().backgroundColor,
        borderColor: rederGraphColors().borderColor,
      },
    ];
    return dataSet;
  };

  return (
    <>
      <div className="underline underline-offset-4 font-semibold text-blue-900 p-3 text-base md:text-lg">{`Suburb sales and rental listings summary for ${propertyAnalysis.PropertyDetails.Suburb} (all bedroom types)`}</div>
      <div className="flex md:flex-row flex-col gap-3">
        <Card title="All Sales Listings" className="text-center w-full md:1/4">
          <div className="panel-box">
            <REIDataTable
              customclass="leading-normal hide-datatable-header text-xs md:text-sm"
              data={propertyAnalysis.AllSalesListings}
              columns={allSalesListingColumns}
              actions={undefined}
            />
          </div>
        </Card>
        <Card
          title={`${propertyAnalysis.PropertyDetails.Category} sales listings`}
          className="text-center w-full md:1/4"
        >
          <div className="text-left p-2 text-xs md:text-sm font-medium">{`${totalUnitSales.current} total`}</div>
          <div className="panel-box ">
            <REIDataTable
              customclass="leading-normal hide-datatable-header text-xs md:text-sm"
              data={propertyAnalysis.SalesPriceRange}
              columns={allSalesListingColumns}
              actions={undefined}
            />
          </div>
        </Card>
        <Card
          title={`${propertyAnalysis.PropertyDetails.Category} rental listings`}
          className="text-center w-full md:1/4"
        >
          <div className="text-left p-2 text-xs md:text-sm font-medium">{`${totalRentalListings.current} total`}</div>
          <div className="panel-box ">
            <REIDataTable
              customclass="leading-normal hide-datatable-header text-xs md:text-sm"
              data={propertyAnalysis.RentalPriceRange}
              columns={rentalListingColumn}
              actions={undefined}
            />
          </div>
        </Card>
        <Card title={graphLabel} className="p-3">
          <div className="flex flex-col gap-2 align-items-center w-full md:1/4">
            {isGraphVisible && (
              <Graph
                id="wla-graph-postcodestats"
                key={propertyAnalysis.PropertyDetails.PropertyId}
                graphType="pie"
                labels={labels}
                customClass="grow-0 justify-center h-6 w-full md:1/4"
                style={{ borderRadius: "6px", height: "200px" }}
                customOptions={customOptions}
                dataSet={renderDataSet()}
              ></Graph>
            )}
            {getPieChartStatistics()}
          </div>
        </Card>
      </div>
    </>
  );
};

export default PropertyAnalysis;
